import {
    api,
    apiErrorMessage,
    getTimezone,
    oneSpec,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ConfirmDialog,
    DateTime,
    DateTimeField,
    PageHeader,
    Textarea,
    UnsavedGuard,
    zonedToIso,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import type { Policy, PolicyKind, PolicyVersion } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { POLICY_NAMES } from '@/pages/policies/index';

/** Keyed by kind so the draft state resets when the route changes. */
/** The reads of `/policies/:kind` (BM-46 §2.0; a version is read when chosen). */
export const policyReads = (kind: string | undefined) => ({
    policy: oneSpec(`/staff/policies/${kind ?? 'terms'}`),
});

export function PolicyPage() {
    const { kind = 'terms' } = useParams();
    return <PolicyEditor key={kind} kind={kind} />;
}

function PolicyEditor({ kind }: { kind: string }) {
    const me = useMe();
    const can = useAuthStore((s) => s.can);
    const query = useOne<Policy>(policyReads(kind).policy.path);
    const [draft, setDraft] = useState<string | null>(null);
    const [publishing, setPublishing] = useState(false);
    // BM-46 §2.6: a day and a time in the staff member's zone; empty means now.
    const [effective, setEffective] = useState<{
        day: string | null;
        time: string | null;
    }>({ day: null, time: null });
    const [reading, setReading] = useState<number | null>(null);
    const version = useOne<PolicyVersion>(
        reading !== null ? `/staff/policies/${kind}/versions/${reading}` : null,
    );
    const [error, setError] = useState<string | null>(null);
    const saveDraft = useWrite(
        async (body: { text: string; version: number }) =>
            (await api().put<Policy>(`/staff/policies/${kind}/draft`, body))
                .data,
        ['/staff/policies'],
    );
    const publish = useWrite(
        async (effective_from: string | undefined) =>
            (
                await api().post<PolicyVersion>(
                    `/staff/policies/${kind}/publish`,
                    effective_from ? { effective_from } : {},
                )
            ).data,
        ['/staff/policies'],
    );
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    const name = POLICY_NAMES[kind as PolicyKind] ?? kind;
    return (
        <div>
            {/* BM-46 §2.9: leaving with unsaved changes asks first */}
            <UnsavedGuard dirty={draft !== null} />
            <PageHeader
                crumbs={[{ label: 'Policies', href: '/policies' }]}
                title={name}
                meta={<AsOfBadge asOf={query.freshness.asOf} />}
            />
            <DetailBody query={query} title={name}>
                {(p) => {
                    const text = draft ?? p.draft_text;
                    const dirty = draft !== null && draft !== p.draft_text;
                    return (
                        <div className="space-y-4">
                            {error ? (
                                <p
                                    role="alert"
                                    className="text-destructive text-sm"
                                >
                                    {error}
                                </p>
                            ) : null}
                            <Card>
                                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                                    <CardTitle className="text-sm font-medium">
                                        Draft
                                        {p.draft_updated_by
                                            ? ` · last edited by ${who(p.draft_updated_by, me)}`
                                            : ''}
                                    </CardTitle>
                                    {can(P.policiesPublish) ? (
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={
                                                    !dirty ||
                                                    saveDraft.isPending
                                                }
                                                onClick={() =>
                                                    saveDraft.mutate(
                                                        {
                                                            text,
                                                            version: p.version,
                                                        },
                                                        {
                                                            onSuccess: () =>
                                                                setDraft(null),
                                                            onError,
                                                        },
                                                    )
                                                }
                                            >
                                                Save draft
                                            </Button>
                                            <Button
                                                size="sm"
                                                disabled={
                                                    dirty ||
                                                    p.draft_text.length === 0
                                                }
                                                onClick={() =>
                                                    setPublishing(true)
                                                }
                                            >
                                                Publish as v
                                                {p.current_version + 1}
                                            </Button>
                                        </div>
                                    ) : null}
                                </CardHeader>
                                <CardContent>
                                    <Textarea
                                        aria-label="Draft text"
                                        rows={18}
                                        className="font-mono text-xs"
                                        value={text}
                                        readOnly={!can(P.policiesPublish)}
                                        onChange={(e) =>
                                            setDraft(e.target.value)
                                        }
                                    />
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-medium">
                                        Published versions
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul
                                        className="divide-y text-sm"
                                        data-testid="policy-versions"
                                    >
                                        {[...p.versions].reverse().map((v) => (
                                            <li
                                                key={v.version}
                                                className="flex flex-wrap items-center justify-between gap-2 py-2"
                                            >
                                                <span>
                                                    <span className="font-medium">
                                                        v{v.version}
                                                    </span>
                                                    {v.version ===
                                                    p.current_version ? (
                                                        <span className="text-muted-foreground ml-1 text-xs">
                                                            (current)
                                                        </span>
                                                    ) : null}{' '}
                                                    · effective{' '}
                                                    <DateTime
                                                        value={v.effective_from}
                                                        empty="—"
                                                    />{' '}
                                                    · {who(v.published_by, me)}
                                                </span>
                                                <span className="flex items-center gap-2">
                                                    <code className="text-muted-foreground text-xs">
                                                        {v.text_hash.slice(
                                                            0,
                                                            12,
                                                        )}
                                                        …
                                                    </code>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() =>
                                                            setReading(
                                                                reading ===
                                                                    v.version
                                                                    ? null
                                                                    : v.version,
                                                            )
                                                        }
                                                    >
                                                        {reading === v.version
                                                            ? 'Hide'
                                                            : 'Read'}
                                                    </Button>
                                                </span>
                                            </li>
                                        ))}
                                        {p.versions.length === 0 ? (
                                            <li className="text-muted-foreground py-2">
                                                Nothing published yet.
                                            </li>
                                        ) : null}
                                    </ul>
                                    {reading !== null && version.data ? (
                                        <pre
                                            className="bg-muted mt-3 max-h-96 overflow-auto rounded p-3 text-xs whitespace-pre-wrap"
                                            data-testid="policy-text"
                                        >
                                            {version.data.data.text}
                                        </pre>
                                    ) : null}
                                </CardContent>
                            </Card>
                            <ConfirmDialog
                                open={publishing}
                                onOpenChange={setPublishing}
                                title={`Publish ${name} v${p.current_version + 1}`}
                                description="The text becomes immutable, policy.published reaches Checkout, and new acceptances record this version."
                                confirmLabel="Publish"
                                busy={publish.isPending}
                                onConfirm={() =>
                                    publish.mutate(
                                        effective.day
                                            ? zonedToIso(
                                                  effective.day,
                                                  effective.time ?? '00:00',
                                                  getTimezone(),
                                              )
                                            : undefined,
                                        {
                                            onSuccess: () =>
                                                setPublishing(false),
                                            onError,
                                        },
                                    )
                                }
                            >
                                <div className="space-y-1">
                                    <label
                                        htmlFor="effective_from"
                                        className="text-sm"
                                    >
                                        Effective from (optional, defaults to
                                        now)
                                    </label>
                                    <DateTimeField
                                        id="effective_from"
                                        value={effective}
                                        onChange={setEffective}
                                    />
                                </div>
                            </ConfirmDialog>
                        </div>
                    );
                }}
            </DetailBody>
        </div>
    );
}
